import { ref, computed } from "vue";
import { defineStore } from "pinia";
import { triggerRebuild, getRebuildStatus } from "@/api/settings";

const POLL_MS = 5000;
const GIVE_UP_MS = 10 * 60 * 1000;

/**
 * Global deploy ("發佈更新") progress tracker. A Pinia store (not a per-view
 * composable) so the state survives route changes, every editor's button
 * disables while a deploy is in flight, and the app-wide DeployBanner can
 * show progress from anywhere.
 *
 * `state` walks: "idle" → "pending" (rebuild dispatched; the server debounces
 * up to 60s, so we wait for a NEW workflow run to appear) → "deploying" →
 * "done" | "failed". "untracked" means a rebuild was triggered but progress
 * can't be followed (GitHub unreachable / gave up polling); "unavailable"
 * means there is no deploy pipeline at all (local dev, no webhook).
 *
 * `init()` (called once by DeployBanner on mount) checks whether the deploy
 * workflow is already running — e.g. after a page refresh, or a deploy
 * started by a colleague — and resumes tracking it.
 */
export const useDeployStore = defineStore("deploy", () => {
  const state = ref("idle");
  const runUrl = ref(null);
  const finishedAt = ref(null);
  const deploying = computed(() => state.value === "pending" || state.value === "deploying");

  let baselineRunId = null;
  let timer = null;
  let giveUpAt = 0;
  let initialized = false;

  function stop() {
    clearTimeout(timer);
    timer = null;
  }

  function startPolling() {
    giveUpAt = Date.now() + GIVE_UP_MS;
    timer = setTimeout(poll, POLL_MS);
  }

  async function poll() {
    let status;
    try {
      status = await getRebuildStatus();
    } catch {
      status = null;
    }
    if (!status || !status.available) {
      state.value = "untracked";
      return;
    }
    const run = status.run;
    if (run && run.id !== baselineRunId) {
      runUrl.value = run.html_url || null;
      if (run.status === "completed") {
        state.value = run.conclusion === "success" ? "done" : "failed";
        finishedAt.value = new Date();
        return;
      }
      state.value = "deploying";
    }
    if (Date.now() > giveUpAt) {
      state.value = "untracked";
      return;
    }
    timer = setTimeout(poll, POLL_MS);
  }

  /**
   * Trigger the rebuild and start tracking it. The state flips to "pending"
   * immediately so 發佈更新 buttons disable on the first click.
   * Returns true when a deploy was dispatched, false when there is no pipeline
   * (local dev), and null when the trigger request itself failed.
   */
  async function trigger() {
    if (deploying.value) return true; // already in flight — don't double-fire
    stop();
    const previous = state.value;
    state.value = "pending";
    runUrl.value = null;
    finishedAt.value = null;
    try {
      // Snapshot the current latest run BEFORE triggering, so we can tell the
      // new run apart from the previous one once it appears.
      let baseline = null;
      try {
        baseline = await getRebuildStatus();
      } catch {
        baseline = null;
      }
      baselineRunId = baseline?.run?.id ?? null;

      const { triggered } = await triggerRebuild();
      if (!triggered) {
        state.value = "unavailable";
        return false;
      }
      if (!baseline || !baseline.available) {
        state.value = "untracked";
        return true;
      }
      startPolling();
      return true;
    } catch {
      state.value = previous;
      return null;
    }
  }

  /** Resume tracking a deploy that is already running (page refresh, or a
   *  colleague's publish). Safe to call repeatedly; only the first call acts. */
  async function init() {
    if (initialized) return;
    try {
      const status = await getRebuildStatus();
      initialized = true;
      const run = status?.run;
      if (status?.available && run && run.status !== "completed") {
        baselineRunId = null; // any run counts — we're adopting it
        runUrl.value = run.html_url || null;
        state.value = "deploying";
        startPolling();
      }
    } catch {
      // Not logged in yet / API unreachable — stay idle and allow a retry.
    }
  }

  /** Hide the banner (used for the terminal states). */
  function dismiss() {
    stop();
    state.value = "idle";
  }

  return { state, runUrl, finishedAt, deploying, trigger, init, dismiss };
});
